/**
 * Helpers d'échappement / construction des recherches texte PostgREST.
 *
 * Tout terme utilisateur interpolé dans une chaîne `.or('col.ilike.%term%,...')`
 * doit passer par `escapeOrSearchTerm`, et idéalement par `buildSearchOrConditions`
 * qui centralise la construction multi-champs (revue 2026-06-01).
 *
 * Deux classes de risque, vérifiées end-to-end via PostgREST :
 *  - Syntaxe `.or()` : `,` sépare les filtres et `(` `)` ouvrent des groupes.
 *    Non échappables dans un motif → un terme contenant `,(` provoque un
 *    HTTP 400 (PGRST100 « failed to parse logic tree »). On les RETIRE.
 *  - Wildcards de motif : côté PostgREST `*` ET `%` jouent le rôle de joker
 *    (`*` est traduit en `%`), `_` matche un caractère. Un terme contenant
 *    `*`/`%`/`_` élargit donc silencieusement la recherche. On les ÉCHAPPE en
 *    littéraux via `\` (ILIKE Postgres + parse PostgREST, vérifié : `4*Trophy`
 *    non échappé matchait les 3 produits ; `4\*Trophy` n'en matche aucun).
 *    `\` lui-même est échappé en premier pour rester littéral.
 */
export function escapeOrSearchTerm(term: string): string {
  return term
    .replace(/[\\%_*]/g, (c) => `\\${c}`) // wildcards (\\ % _ *) -> littéraux
    .replace(/[(),]/g, ''); // caractères de syntaxe .or() -> retirés
}

/**
 * Échappe une valeur pour la forme MÉTHODE supabase-js `.ilike(col, pattern)`
 * (≠ chaîne `.or(...)`). La lib paramètre la valeur, donc `, ( ) "` y sont déjà
 * LITTÉRAUX — rien à neutraliser de ce côté. Seuls `%` et `_` sont des wildcards
 * SQL LIKE ; `*` est un caractère LITTÉRAL en LIKE (contrairement à la forme
 * chaîne `.or()` où PostgREST traduit `*`→`%`). On échappe donc `\ % _` mais
 * PAS `*`. Garantit que le mode AND (méthode) et le mode OR (chaîne) traitent
 * `% _` à l'identique (revue 2026-06-01).
 */
export function escapeLikeMethod(value: string): string {
  return value.replace(/[\\%_]/g, (c) => `\\${c}`);
}

/**
 * Condition PostgREST TOUJOURS FAUSSE pour un champ donné (`field IS NULL ET
 * field IS NOT NULL`). Field-agnostique (tout type de colonne), vérifiée e2e :
 * 0 ligne. Sert de "match-nothing" quand un terme de recherche / filtre se
 * réduit à rien après échappement, pour ne pas tomber dans un `ilike.%%`
 * (match-all) ou un filtre purement supprimé (revue 2026-06-01).
 */
export function alwaysFalseCondition(field: string): string {
  return `and(${field}.is.null,${field}.not.is.null)`;
}

/**
 * Retour réunion 07/07 (E) : le n° BED a « le même format que le BPI avec BED au
 * milieu » (`BED-<année>-<n° équipage>` ↔ `BPI-<année>-<n° équipage>`). Plutôt
 * que de matérialiser une colonne `bed_code` dédiée, on recherche un n° BED en
 * substituant `BED`→`BPI` dans le terme et en le cherchant sur `bpi_code`
 * (crews_filter_view et, après migration 00151, contacts_filter_view). Le variant
 * n'est produit que si le terme ressemble à un code BED (« bed » suivi, après
 * séparateur optionnel, d'un chiffre) → un nom comme « Bedard » n'en génère pas.
 *
 * Ex : 'BED-2027-0042' -> ['BED-2027-0042', 'BPI-2027-0042']
 */
export function expandBpiBedTerms(rawTerm: string): string[] {
  const terms = [rawTerm];
  if (/bed[\s\-_]*\d/i.test(rawTerm)) {
    const bpiVariant = rawTerm.replace(/bed/i, 'BPI');
    if (bpiVariant !== rawTerm) terms.push(bpiVariant);
  }
  return terms;
}

/**
 * Construit la chaîne de conditions `.or()` pour une recherche « contient »
 * (ILIKE %mot%) sur plusieurs champs. Le terme est découpé en mots (espaces) :
 * chaque mot doit matcher au moins un champ (AND de groupes OR), si bien que
 * « jean dupont » trouve first_name=Jean / last_name=Dupont. Un seul mot garde
 * la forme plate historique. Chaque mot est échappé UNE fois ; les noms de
 * champs ne le sont jamais (config développeur, pas une entrée utilisateur).
 * À passer directement à `query.or(...)`.
 *
 * Retour 07/07 (E) : un terme n° BED est étendu à son équivalent BPI (cf.
 * expandBpiBedTerms) → les conditions couvrent bpi_code pour tous les champs.
 *
 * Ex : buildSearchOrConditions(['first_name','email'], 'a,b')
 *      -> 'first_name.ilike.%ab%,email.ilike.%ab%'
 *      buildSearchOrConditions(['first_name','last_name'], 'jean dupont')
 *      -> 'and(or(first_name.ilike.%jean%,last_name.ilike.%jean%),or(first_name.ilike.%dupont%,last_name.ilike.%dupont%))'
 */
export function buildSearchOrConditions(fields: readonly string[], rawTerm: string): string {
  if (fields.length === 0) return '';
  // L'expansion BED→BPI doit voir le terme complet AVANT la tokenisation pour
  // conserver les séparateurs avec espaces (`BED 0042`, `BED 2027 0042`). Les
  // mots de chaque terme étendu sont ensuite regroupés par position : BED et
  // BPI rejoignent ainsi le même groupe OR.
  const allWordVariants: string[][] = [];
  for (const expandedTerm of expandBpiBedTerms(rawTerm)) {
    const safeWords = expandedTerm
      .split(/\s+/)
      .map(escapeOrSearchTerm)
      .filter((word) => word !== '');

    safeWords.forEach((word, index) => {
      const variants = allWordVariants[index] ?? [];
      if (!variants.includes(word)) variants.push(word);
      allWordVariants[index] = variants;
    });
  }

  // Borne le nombre de mots : au-delà, l'URL PostgREST générée (mots × champs
  // conditions) deviendrait démesurée ; ignorer les mots excédentaires ne fait
  // qu'élargir les résultats (jamais en perdre).
  const wordVariants = allWordVariants.slice(0, 10);
  if (wordVariants.length === 0) {
    // Terme entièrement composé de blancs et/ou de caractères de syntaxe .or()
    // (`, ( )`) : il ne reste aucun contenu cherchable après nettoyage. On émet
    // une condition TOUJOURS FAUSSE plutôt qu'un `ilike.%%` qui matcherait
    // toutes les lignes (revue 2026-06-01).
    return alwaysFalseCondition(fields[0]);
  }
  const orGroup = (variants: string[]) =>
    fields.flatMap((field) => variants.map((variant) => `${field}.ilike.%${variant}%`)).join(',');
  if (wordVariants.length === 1) {
    return orGroup(wordVariants[0]);
  }
  return `and(${wordVariants.map((variants) => `or(${orGroup(variants)})`).join(',')})`;
}
